import { SuccessfullAddPoleDialog } from '../components/poles/successfullAddPoleDialog';
import { AddPoleDialog } from '../components/poles/addPoleDialog';
import { useState, useCallback, useEffect } from 'react';
import PolePageHeader from '../components/poles/polePageHeader';
import PoleFiltration from '../components/poles/poleFiltration';
import { CustomErrorLogger } from '../utils/CustomErrorLogger';
import { Divider, Box, LinearProgress } from '@mui/material';
import { useHasPermission } from '../hooks/useHasPermission';
import { isObjectEmpty } from '../utils/isObjectEmpty';
import {
    ListNavigationProvider,
    useListPageNavigation,
} from '../hooks/listNavigation';
import PoleList from '../components/poles/poleList';
import PoleGrid from '../components/poles/poleGrid';
import NoRecord from '../components/NoRecord';
import Page from '../components/Page';
import axios from 'axios';
import '../css/roles/roles.css';

const Poles = () => {

    const hasPoleCreatePermission = useHasPermission('pole_create');

    const [openAddPole, setOpenAddPole] = useState(false);
    const [openSuccessfulAddPole, setOpenSuccessfulAddPole] = useState(false);

    const [loading, setLoading] = useState(true);
    const [poles, setPoles] = useState([])
    const [totalPoles, setTotalPoles] = useState('');
    const [filteredPoleCount, setFilteredPoleCount] = useState('');
    const [poleFetched, setPoleFetched] = useState(0);
    const [selectedDisplayMode, setSelectedDisplayMode] = useState(JSON.parse(localStorage.getItem('selectedPolesDisplayMode')) || 'grid');
    const [poleFilters, setPoleFilters] = useState(
        JSON.parse(localStorage.getItem('poleFilters')) ||
        { debouncedSearch: '' }
    );
    const defaultSortBy = {
        name: null,
        description: null,
    };
    
    const [sortBy, setSortBy] = useState(() => {
        const storedSortBy = JSON.parse(localStorage.getItem('poleSortBy')) || {};
        return { ...defaultSortBy, ...storedSortBy }; // Merge defaults with stored sortBy
    });
    
    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'poles',
        pathname: '/dashboard/poles',
        displayMode: selectedDisplayMode,
        itemSizes: { grid: 280, list: 70 },
        resetOnFilters: poleFilters,
        resetOnSort: sortBy,
        ready: !loading,
    });
    const limit = 30;

    const filtersEmpty = isObjectEmpty(poleFetched);

    useEffect(() => {
        setLoading(true)
    }, [poleFilters]);

    useEffect(() => {
        localStorage.setItem('poleFilters', JSON.stringify(poleFilters));
    }, [poleFilters]);

    useEffect(() => {
        localStorage.setItem('poleSortBy', JSON.stringify(sortBy));
    }, [sortBy]);

    useEffect(() => {
        localStorage.setItem('selectedPolesDisplayMode', JSON.stringify(selectedDisplayMode));
    }, [selectedDisplayMode]);

    const fetchPoles = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('pole/list', { params: { offset: adjustedOffset, limit, filters: poleFilters, sortBy } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedPoles = [];
                // Update the fetched records in their correct positions
                response?.data?.poles.forEach((pole, index) => { updatedPoles[adjustedOffset + index] = pole });
                // Update the state with the new poles array
                setPoles(updatedPoles);
                setFilteredPoleCount(response?.data?.filteredPolesCount);
                setTotalPoles(response?.data?.totalPoles);
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching poles:', error);
            CustomErrorLogger(error, 'fetch poles in poles page');
        }
    }, [offset, poleFilters, sortBy]);

    useEffect(() => {
        fetchPoles()
    }, [fetchPoles]);


    return (
        <ListNavigationProvider value={navigationValue}>
        <Page title="Pôles" list={true}>
            <PolePageHeader
                setOpenAddPole={setOpenAddPole}
                hasPoleCreatePermission={hasPoleCreatePermission}
            />
            <Divider />

            {loading ?
                <Box>
                    <PoleFiltration
                        filtersEmpty={filtersEmpty}
                        poleFilters={poleFilters}
                        setPoleFilters={setPoleFilters}
                        totalPoles={totalPoles}
                        poleFetched={poleFetched}
                        filteredPoleCount={filteredPoleCount}
                        selectedDisplayMode={selectedDisplayMode}
                        setSelectedDisplayMode={setSelectedDisplayMode}
                    />
                    <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
                </Box> :
                totalPoles === 0 ?
                    <NoRecord
                        description="Aucun pôle ajouté pour le moment"
                        buttonTitle="Ajouter un pôle"
                        handleOpenDialog={() => setOpenAddPole(true)}
                        hasPermission={hasPoleCreatePermission}
                    /> :
                    <Box>
                        <PoleFiltration
                            filtersEmpty={filtersEmpty}
                            poleFilters={poleFilters}
                            setPoleFilters={setPoleFilters}
                            totalPoles={totalPoles}
                            poleFetched={poleFetched}
                            filteredPoleCount={filteredPoleCount}
                            selectedDisplayMode={selectedDisplayMode}
                            setSelectedDisplayMode={setSelectedDisplayMode}
                        />

                        {selectedDisplayMode === 'grid' ? (
                            <PoleGrid
                                scrollRef={scrollRef}
                                poles={poles}
                                setOffset={setOffset}
                                filteredPoleCount={filteredPoleCount}
                                setPoleFetched={setPoleFetched}
                            />
                        ) : (
                            <PoleList
                                scrollRef={scrollRef}
                                poles={poles}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                setOffset={setOffset}
                                filteredPoleCount={filteredPoleCount}
                                setPoleFetched={setPoleFetched}
                            />
                        )}

                    </Box>
            }

            {openAddPole &&
                <AddPoleDialog
                    setPoles={setPoles}
                    setTotalPoles={setTotalPoles}
                    setFilteredPoleCount={setFilteredPoleCount}
                    openAddPole={openAddPole}
                    setOpenAddPole={setOpenAddPole}
                />
            }

            {openSuccessfulAddPole &&
                <SuccessfullAddPoleDialog
                    openSuccessfulAddPole={openSuccessfulAddPole}
                    setOpenAddPole={setOpenAddPole}
                />
            }

        </Page>
        </ListNavigationProvider>
    );
};

export default Poles;